import { useMemo, useState } from 'react';

import type { ForecastFilters } from '@/features/forecasts/components/explorer/filters/ForecastFiltersUrl';
import { useForecastFacets } from '@/features/forecasts/hooks/useForecastFacets';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { formatIsoDateLabel } from '@/utils/date';

type FilterChip = { label: string; color: 'forecast' | 'product' | 'customer' };

function buildFilterChips(filters: ForecastFilters): FilterChip[] {
  const chips: FilterChip[] = [];

  if (filters.forecastStart || filters.forecastEnd) {
    const start = filters.forecastStart ? formatIsoDateLabel(filters.forecastStart) : '…';
    const end = filters.forecastEnd ? formatIsoDateLabel(filters.forecastEnd) : '…';
    chips.push({ label: `Forecast window: ${start} – ${end}`, color: 'forecast' });
  }
  if (filters.leadTime) chips.push({ label: `Lead time: ${filters.leadTime}`, color: 'forecast' });
  if (filters.span) chips.push({ label: `Span: ${filters.span}`, color: 'forecast' });
  if (filters.product) chips.push({ label: `Product: ${filters.product}`, color: 'product' });
  if (filters.customer) chips.push({ label: `Customer: ${filters.customer}`, color: 'customer' });

  return chips;
}

export function useForecastFilterControls() {
  const { state: appliedFilters, setState, clearState, query, setSearch } = useForecastQueryState();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<ForecastFilters>({});

  const { facets, isLoading: facetsLoading } = useForecastFacets({ enabled: filtersOpen });

  const filterChips = useMemo(() => buildFilterChips(appliedFilters), [appliedFilters]);
  const hasSearch = query.trim().length > 0;

  function openFilters() {
    setDraftFilters(appliedFilters);
    setFiltersOpen(true);
  }

  function applyDraft() {
    setState(draftFilters);
    setFiltersOpen(false);
  }

  function clearDraft() {
    setDraftFilters({});
  }

  function clearAppliedFilters() {
    clearState();
    setDraftFilters({});
  }

  function clearSearch() {
    setSearch('');
  }

  return {
    appliedFilters,
    draftFilters,
    setDraftFilters,
    facets,
    facetsLoading,
    filtersOpen,
    setFiltersOpen,
    filterChips,
    filterCount: filterChips.length,
    hasSearch,
    query,
    openFilters,
    applyDraft,
    clearDraft,
    clearAppliedFilters,
    clearSearch,
  };
}
